import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
} from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { database } from "../../database/firebase";
import { GAME_MODE } from "../../game/connectFour";
import {
  buildFreshConnect4ForRematch,
  getConnect4LeaderboardRef,
  parseLeaderboardDoc,
} from "../../game/connect4Series";
import {
  isCpuModeSelected,
  loadCpuSeries,
  startFreshCpuGame,
} from "../../game/gameModeStorage";
import { CPU_MAX_GAMES, fetchCpuStatus } from "../../functions/connect4Cpu";
import { isFromMobileApp, postMobileNavigateBack } from "../../utils/mobileBridge";
import "./leaderboard.css";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-9]$/.test(value) ? value : "c1";
}

const CPU_AVATAR_SRC = `${process.env.PUBLIC_URL}/img/computer.png`;

export default function Leaderboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useMemo(() => readStoredUserData(), []);
  const userId = String(
    userData?.userId ?? userData?.userid ?? userData?.id ?? ""
  ).trim();
  const sessionId = String(
    userData?.sessionId || userData?.sessionID || userData?.session_id || ""
  ).trim();
  const lobbyName = String(
    userData?.lobby ||
      userData?.lobbyName ||
      userData?.lobby_name ||
      location.state?.lobbyName ||
      location.state?.lobby ||
      ""
  ).trim();

  const [series, setSeries] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rematchBusy, setRematchBusy] = useState(false);
  const [rematchError, setRematchError] = useState("");
  const [serverGamesPlayed, setServerGamesPlayed] = useState(null);
  const isLocalCpu = isCpuModeSelected();

  const leaderboardRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return getConnect4LeaderboardRef(database, sessionId, lobbyName);
  }, [sessionId, lobbyName]);

  const connect4Ref = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(
      database,
      `lobbies/${sessionId}/lobby/${lobbyName}/state/connect4`
    );
  }, [sessionId, lobbyName]);

  const lobbyRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
  }, [sessionId, lobbyName]);

  useEffect(() => {
    if (isLocalCpu) {
      const stored = loadCpuSeries();
      setSeries(stored ? parseLeaderboardDoc(stored) : null);
      setLoading(false);
      return undefined;
    }

    if (!sessionId || !lobbyName) {
      setLoading(false);
      return undefined;
    }

    const usersRef = collection(
      database,
      `lobbies/${sessionId}/lobby/${lobbyName}/users`
    );

    const unsubUsers = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        setPlayers(
          snap.docs.map((d) => ({ id: d.id, ...d.data() })).slice(0, 2)
        );
      }
    );

    const unsubLb = onSnapshot(
      leaderboardRef,
      (snap) => {
        setSeries(snap.exists() ? parseLeaderboardDoc(snap.data()) : null);
        setLoading(false);
      },
      () => setLoading(false)
    );

    const unsubLobby = onSnapshot(lobbyRef, (snap) => {
      const status = String(snap.data()?.status ?? "").toLowerCase();
      if (status === "play") {
        navigate("/game", { replace: true });
      }
    });

    return () => {
      unsubUsers();
      unsubLb();
      unsubLobby();
    };
  }, [isLocalCpu, leaderboardRef, lobbyName, lobbyRef, navigate, sessionId]);

  // Local series total covers the case where the just-finished game's
  // report hasn't reached the backend yet.
  const localGamesPlayed =
    Number(series?.player1Wins || 0) +
    Number(series?.player2Wins || 0) +
    Number(series?.draws || 0);
  const cpuGamesPlayed = Math.max(serverGamesPlayed ?? 0, localGamesPlayed);
  const cpuGamesLeft = Math.max(0, CPU_MAX_GAMES - cpuGamesPlayed);

  useEffect(() => {
    if (!isLocalCpu) return undefined;
    let active = true;
    fetchCpuStatus()
      .then((s) => active && setServerGamesPlayed(Number(s.gamesPlayed) || 0))
      .catch((e) => console.error("Could not load CPU game status:", e));
    return () => {
      active = false;
    };
  }, [isLocalCpu]);

  const handleFinish = () => {
    if (isFromMobileApp(userData)) {
      postMobileNavigateBack();
      return;
    }
    window.location.href = process.env.REACT_APP_BASE_URL || "/";
  };

  const handleCpuPlayAgain = async () => {
    if (rematchBusy) return;
    setRematchBusy(true);
    setRematchError("");
    try {
      const status = await fetchCpuStatus();
      const played = Math.max(Number(status.gamesPlayed) || 0, localGamesPlayed);
      setServerGamesPlayed(Number(status.gamesPlayed) || 0);
      if (played >= CPU_MAX_GAMES) return;

      const name =
        String(userData?.name ?? userData?.userName ?? "").trim() ||
        "You";
      startFreshCpuGame(userId, name);
      navigate("/game", { replace: true });
    } catch (e) {
      console.error("CPU rematch failed:", e);
      setRematchError("Could not start a new game. Try again.");
    } finally {
      setRematchBusy(false);
    }
  };

  const player1 = useMemo(
    () =>
      players.find(
        (p) =>
          String(p?.userId ?? p?.id ?? "").trim() === series?.player1UserId
      ) || players[0] || null,
    [players, series?.player1UserId]
  );

  const player2 = useMemo(
    () =>
      players.find(
        (p) =>
          String(p?.userId ?? p?.id ?? "").trim() === series?.player2UserId
      ) || players[1] || null,
    [players, series?.player2UserId]
  );

  const p1Name = series?.player1Name || "Player 1";
  const p2Name = series?.player2Name || "Player 2";
  const p1Wins = Number(series?.player1Wins || 0);
  const p2Wins = Number(series?.player2Wins || 0);

  const p1AvatarSrc = isLocalCpu
    ? `/img/${normalizeCharacterName(userData?.character)}.png`
    : player1?.character
      ? `/img/${normalizeCharacterName(player1.character)}.png`
      : "";

  const p2AvatarSrc = isLocalCpu
    ? CPU_AVATAR_SRC
    : player2?.character
      ? `/img/${normalizeCharacterName(player2.character)}.png`
      : "";

  const rematch = series?.rematch || { status: "idle", requestedBy: "", confirmed: {} };
  const iRequested = rematch.requestedBy === userId;
  const opponentId =
    userId === series?.player1UserId
      ? series?.player2UserId
      : userId === series?.player2UserId
        ? series?.player1UserId
        : "";
  const iConfirmed = Boolean(rematch.confirmed?.[userId]);
  const opponentConfirmed = opponentId
    ? Boolean(rematch.confirmed?.[opponentId])
    : false;
  const rematchPending = rematch.status === "pending";

  const handleRematchAction = async () => {
    if (!leaderboardRef || !series || !userId || rematchBusy) return;
    setRematchBusy(true);
    setRematchError("");

    try {
      let shouldNavigate = false;

      await runTransaction(database, async (tx) => {
        const lbSnap = await tx.get(leaderboardRef);
        const lb = parseLeaderboardDoc(
          lbSnap.exists() ? lbSnap.data() : series
        );
        if (!lb) throw new Error("Leaderboard missing");

        const confirmed = { ...(lb.rematch?.confirmed || {}) };
        confirmed[userId] = true;

        const p1 = lb.player1UserId;
        const p2 = lb.player2UserId;

        if (confirmed[p1] && confirmed[p2]) {
          const c4Snap = await tx.get(connect4Ref);
          const mode = c4Snap.exists()
            ? String(c4Snap.data()?.mode || GAME_MODE.MULTIPLAYER)
            : GAME_MODE.MULTIPLAYER;
          const cpuPlayer = c4Snap.exists()
            ? Number(c4Snap.data()?.cpuPlayer) || 2
            : 2;
          const fresh = buildFreshConnect4ForRematch(lb, mode, cpuPlayer);
          tx.set(connect4Ref, fresh);
          tx.set(leaderboardRef, {
            ...lb,
            rematch: { status: "idle", requestedBy: "", confirmed: {} },
          });
          tx.set(
            lobbyRef,
            { status: "play", gameType: "connect4" },
            { merge: true }
          );
          shouldNavigate = true;
        } else {
          tx.set(leaderboardRef, {
            ...lb,
            rematch: {
              status: "pending",
              requestedBy: lb.rematch?.requestedBy || userId,
              confirmed,
            },
          });
        }
      });

      if (shouldNavigate) {
        navigate("/game", { replace: true });
      }
    } catch (e) {
      console.error("Rematch failed:", e);
      setRematchError("Could not update rematch. Try again.");
    } finally {
      setRematchBusy(false);
    }
  };

  if (!isLocalCpu && (!sessionId || !lobbyName)) {
    return (
      <div className="bnb-leaderboard-page">
        <div className="bnb-empty-state">Missing lobby/session details.</div>
      </div>
    );
  }

  return (
    <div className="bnb-leaderboard-page bnb-leaderboard-page--connect4">
      {loading ? (
        <div className="bnb-empty-state">Loading leaderboard...</div>
      ) : !series ? (
        <div className="bnb-empty-state">No match results yet.</div>
      ) : (
        <div className="c4-series-panel">
          <h1 className="c4-series-title">MATCH RESULTS</h1>

          <div className="c4-series-scoreboard">
            <div className="c4-series-player c4-series-player--left">
              <div className="c4-series-avatar">
                {p1AvatarSrc ? (
                  <img src={p1AvatarSrc} alt="" />
                ) : (
                  <span>♟</span>
                )}
              </div>
              <p className="c4-series-name">{p1Name}</p>
            </div>

            <div className="c4-series-score" aria-label={`Score ${p1Wins} to ${p2Wins}`}>
              <span className="c4-series-score-num">{p1Wins}</span>
              <span className="c4-series-score-sep">-</span>
              <span className="c4-series-score-num">{p2Wins}</span>
            </div>

            <div className="c4-series-player c4-series-player--right">
              <div
                className={`c4-series-avatar ${
                  isLocalCpu ? "c4-series-avatar--cpu" : ""
                }`}
              >
                {p2AvatarSrc ? (
                  <img src={p2AvatarSrc} alt="" />
                ) : (
                  <span>♟</span>
                )}
              </div>
              <p className="c4-series-name">{p2Name}</p>
            </div>
          </div>

          {series.draws > 0 ? (
            <p className="c4-series-draws">Draws: {series.draws}</p>
          ) : null}

          <div className="c4-rematch-block">
            {isLocalCpu ? (
              cpuGamesLeft > 0 ? (
                <>
                  <p className="c4-rematch-status">
                    Games left: {cpuGamesLeft} of {CPU_MAX_GAMES}
                  </p>
                  <button
                    type="button"
                    className="c4-rematch-btn"
                    disabled={rematchBusy}
                    onClick={handleCpuPlayAgain}
                  >
                    Play Again
                  </button>
                </>
              ) : (
                <>
                  <p className="c4-rematch-status">
                    You have played all {CPU_MAX_GAMES} games.
                  </p>
                  <button
                    type="button"
                    className="c4-rematch-btn"
                    onClick={handleFinish}
                  >
                    Finish
                  </button>
                </>
              )
            ) : !rematchPending ? (
              <button
                type="button"
                className="c4-rematch-btn"
                disabled={rematchBusy}
                onClick={handleRematchAction}
              >
                Play Again
              </button>
            ) : iRequested && !opponentConfirmed ? (
              <p className="c4-rematch-status">Waiting for opponent to accept…</p>
            ) : !iConfirmed && opponentId ? (
              <>
                <p className="c4-rematch-status">Opponent wants a rematch!</p>
                <button
                  type="button"
                  className="c4-rematch-btn c4-rematch-btn--accept"
                  disabled={rematchBusy}
                  onClick={handleRematchAction}
                >
                  Accept Rematch
                </button>
              </>
            ) : (
              <p className="c4-rematch-status">Confirming rematch…</p>
            )}
            {rematchError ? (
              <p className="c4-rematch-error" role="alert">
                {rematchError}
              </p>
            ) : null}
          </div>

          {!isLocalCpu || cpuGamesLeft > 0 ? (
            <button
              type="button"
              className="c4-lobby-btn"
              onClick={() => navigate(isLocalCpu ? "/modes" : "/lobby")}
            >
              {isLocalCpu ? "Back to Menu" : "Back to Lobby"}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
